import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Paperclip,
  UploadCloud,
  X,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";
import { useAuth } from "../../../../../context/auth-context";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "semanal"
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OperacionalKey =
  | "ok"
  | "pendente_anexo"
  | "vencida"
  | "renovacao_pendente"
  | "aguardando_validacao"
  | "pendente"
  | "nao_exigido";

type RastreabilidadeStatus =
  | "vigente"
  | "substituido"
  | "vencido"
  | "aguardando_validacao"
  | "cancelado";

type PortalStatusOperacional =
  | "atualizado"
  | "pendente_anexo"
  | "renovacao_pendente"
  | "vencido"
  | "aguardando_validacao"
  | "nao_exigido"
  | string;

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  renovacaoPendente: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key: OperacionalKey;
  label: string;
  background: string;
  color: string;
  border: string;
};

type DocumentoApi = {
  id: number;
  empresaId?: number | string | null;
  moduloId?: number | string | null;
  nome?: string | null;
  categoria?: string | null;
  orgaoEmissor?: string | null;
  tipoValidade?: TipoValidade | string | null;
  periodicidade?: Periodicidade | string | null;
  areaResponsavel?: string | null;
  responsavelNome?: string | null;
  arquivoVigenteId?: number | string | null;
  arquivoNome?: string | null;
  s3Bucket?: string | null;
  s3Key?: string | null;
  dataEmissao?: string | null;
  dataValidade?: string | null;
  dataUpload?: string | null;
  statusValidade?: StatusValidade | string | null;
  anexoDisponivel?: boolean | null;
  portais?: {
    totalExigem?: number | string | null;
    anexadosAtualizados?: number | string | null;
    pendenteAnexo?: number | string | null;
    renovacaoPendente?: number | string | null;
    emRenovacao?: number | string | null;
    vencidos?: number | string | null;
    aguardandoValidacao?: number | string | null;
  } | null;
};

type DocumentoArquivoApi = {
  id: number;
  documentoId?: number | string | null;
  empresaId?: number | string | null;
  moduloId?: number | string | null;
  versao?: number | string | null;
  vigente?: boolean | null;
  obsoleto?: boolean | null;
  dataEmissao?: string | null;
  dataValidade?: string | null;
  s3Bucket?: string | null;
  s3Key?: string | null;
  mimeType?: string | null;
  originalName?: string | null;
  sizeBytes?: number | string | null;
  observacao?: string | null;
  criadoEm?: string | null;
  criadoPor?: number | string | null;
  substituidoEm?: string | null;
  substituidoPor?: number | string | null;
};

type DocumentoPortalApi = {
  id: number;
  documentoId?: number | string | null;
  empresaClienteId?: number | string | null;
  empresaClientePortalId?: number | string | null;
  exigeDocumento?: boolean | null;
  statusOperacional?: PortalStatusOperacional | null;
  arquivoId?: number | string | null;
  atualizadoEm?: string | null;
  observacao?: string | null;
};

type DocumentoEventoApi = {
  id: number;
  documentoId?: number | string | null;
  arquivoId?: number | string | null;
  portalId?: number | string | null;
  tipoEvento?: string | null;
  titulo?: string | null;
  detalhe?: string | null;
  meta?: Record<string, any> | null;
  criadoEm?: string | null;
  criadoPor?: number | string | null;
};

type ApiDetalheResponse = {
  ok?: boolean;
  documento?: DocumentoApi | null;
  arquivoVigente?: DocumentoArquivoApi | null;
  rastreabilidade?: DocumentoArquivoApi[];
  portais?: DocumentoPortalApi[];
  eventos?: DocumentoEventoApi[];
  erro?: string;
};

type ApiPresignUploadResponse = {
  ok?: boolean;
  bucket?: string;
  key?: string;
  uploadUrl?: string;
  expiresIn?: number;
  mimeType?: string;
  originalName?: string;
  sizeBytes?: number;
  erro?: string;
};

type ApiRegistrarAnexoResponse = {
  ok?: boolean;
  arquivo?: DocumentoArquivoApi;
  erro?: string;
};

type ApiAnexoUrlResponse = {
  ok?: boolean;
  url?: string;
  expiresIn?: number;
  arquivo?: {
    id: number;
    documentoId?: number;
    originalName?: string;
    mimeType?: string;
  };
  erro?: string;
};

type DocumentoDetalhe = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;
  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload: string | null;
  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;
  areaResponsavel: string;
  responsavelNome?: string | null;
  statusValidade: StatusValidade;
  anexoDisponivel: boolean;
  arquivoVigenteId?: number | null;
  arquivoNome?: string | null;
  s3Key?: string | null;
  observacao?: string | null;
  portais: PortalResumo;
};

type DocumentoVersao = {
  id: number;
  documentoId: number;
  versao: number;
  arquivoNome: string;
  s3Bucket: string | null;
  s3Key: string;
  mimeType: string | null;
  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload: string;
  responsavelUpload: string;
  vigente: boolean;
  obsoleto: boolean;
  observacao?: string | null;
};

type DocumentoRastreabilidade = DocumentoVersao & {
  statusRastreabilidade: RastreabilidadeStatus;
  titulo: string;
  descricaoStatus: string;
};

type PortalRow = {
  key: string;
  label: string;
  value: number;
  clientes: Array<{
    id: number | string;
    nome: string;
    portal: string;
    url?: string | null;
    observacao?: string | null;
  }>;
  tone: "neutral" | "success" | "warning" | "danger" | "info" | "purple";
};

type ViewerState = {
  open: boolean;
  loading: boolean;
  url: string | null;
  mimeType: string | null;
  fileName: string;
  error: string | null;
};

const API_BASE = "https://api.logvi-havox.com";
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;
const API_PRESIGN_UPLOAD_URL = `${API_BASE}/template/governanca/documentos/anexos/presign-upload`;
const API_REGISTRAR_ANEXO_URL = `${API_BASE}/template/governanca/documentos/anexos/registrar`;

const BASE_PATH = "/governanca-qualidade-e-compliance/certidoes-e-documentos";
const DEPARTMENT_COLOR = "#2f3f59";

const initialViewerState: ViewerState = {
  open: false,
  loading: false,
  url: null,
  mimeType: null,
  fileName: "",
  error: null,
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function normalizeApiDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    const [, year, month, day] = isoDate;
    return `${day}/${month}/${year}`;
  }

  return raw;
}

function normalizeApiDateTime(value: string | null | undefined): string {
  if (!value) return "Não informado";

  const raw = String(value).trim();
  if (!raw) return "Não informado";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);

  if (iso) {
    const [, yyyy, mm, dd, hh, min] = iso;
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  return raw;
}

function parseDateFlexible(value: string | null): Date | null {
  if (!value) return null;

  const raw = String(value).trim();

  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (br) {
    const [, dd, mm, yyyy] = br;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (iso) {
    const [, yyyy, mm, dd] = iso;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatMesesDias(totalDias: number) {
  const diasAbsolutos = Math.abs(totalDias);

  if (diasAbsolutos < 30) return null;

  const meses = Math.floor(diasAbsolutos / 30);
  const dias = diasAbsolutos % 30;

  const mesesTexto = meses === 1 ? "1 mês" : `${meses} meses`;
  const diasTexto = dias === 1 ? "1 dia" : `${dias} dias`;

  if (dias === 0) return mesesTexto;

  return `${mesesTexto} e ${diasTexto}`;
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeTipoValidade(value: unknown): TipoValidade {
  const raw = String(value || "").trim();

  if (
    raw === "com_vencimento" ||
    raw === "sem_vencimento" ||
    raw === "sob_demanda" ||
    raw === "revisao_periodica"
  ) {
    return raw;
  }

  return "com_vencimento";
}

function normalizePeriodicidade(value: unknown): Periodicidade {
  const raw = String(value || "").trim();

  if (
    raw === "semanal" ||
    raw === "mensal" ||
    raw === "trimestral" ||
    raw === "semestral" ||
    raw === "anual" ||
    raw === "sem_validade" ||
    raw === "sob_demanda"
  ) {
    return raw;
  }

  return "sob_demanda";
}

function normalizeStatusValidade(value: unknown): StatusValidade {
  const raw = String(value || "").trim();

  if (
    raw === "regular" ||
    raw === "a_vencer" ||
    raw === "vencido" ||
    raw === "sem_validade"
  ) {
    return raw;
  }

  return "sem_validade";
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") {
    return null;
  }

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  const direct =
    sessao?.modulo_id ??
    sessao?.moduloId ??
    sessao?.modulo_atual_id ??
    sessao?.moduloSelecionadoId ??
    sessao?.modulo?.id;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const isGovernancaDepartment = (dep: any) => {
    const depSlug = normalizeText(dep?.slug);
    const depNome = normalizeText(dep?.nome);
    const depRouteKey = normalizeText(dep?.routeKey ?? dep?.route_key);
    const depCodigo = normalizeText(dep?.codigo);

    return (
      depSlug.includes("governanca") ||
      depSlug.includes("governança") ||
      depNome.includes("governanca") ||
      depNome.includes("governança") ||
      depNome.includes("compliance") ||
      depRouteKey.includes("governanca") ||
      depRouteKey.includes("governança") ||
      depCodigo.includes("governanca") ||
      depCodigo.includes("governança")
    );
  };

  const isCertidoesModule = (mod: any) => {
    const id = mod?.id;

    if (id === undefined || id === null || id === "") {
      return false;
    }

    const codigo = normalizeText(mod?.codigo);
    const label = normalizeText(mod?.label);
    const nome = normalizeText(mod?.nome);
    const routeKey = normalizeText(mod?.routeKey ?? mod?.route_key);
    const actionKey = normalizeText(mod?.actionKey ?? mod?.action_key);

    return (
      codigo.includes("certidoes") ||
      codigo.includes("certidões") ||
      codigo.includes("documentos") ||
      codigo.includes("certidoes-e-documentos") ||
      codigo.includes("certidoes_documentos") ||
      label.includes("certidoes") ||
      label.includes("certidões") ||
      label.includes("documentos") ||
      nome.includes("certidoes") ||
      nome.includes("certidões") ||
      nome.includes("documentos") ||
      routeKey.includes("certidoes") ||
      routeKey.includes("certidões") ||
      routeKey.includes("documentos") ||
      actionKey.includes("certidoes") ||
      actionKey.includes("certidões") ||
      actionKey.includes("documentos")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const moduloCertidoes =
        modulos.find((mod: any) => isCertidoesModule(mod)) || modulos[0];

      const id = moduloCertidoes?.id;

      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);
  if (fromDepartamentosDiretos) return fromDepartamentosDiretos;

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );

    if (fromEnderecoSelecionado) return fromEnderecoSelecionado;
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);
    if (fromEndereco) return fromEndereco;
  }

  return null;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "portal" : "portais";
}

function formatNullableDate(value: string | null | undefined) {
  return normalizeApiDate(value) || "Não informado";
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "semanal":
      return "Semanal";
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getPrazoOperacional(item: DocumentoDetalhe) {
  if (item.tipoValidade === "sem_vencimento") return "Sem vencimento";
  if (item.tipoValidade === "sob_demanda") return "Sob demanda";

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseDateFlexible(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) return "Validade não informada";

  const mesesDias = formatMesesDias(dias);
  const complemento = mesesDias ? ` (${mesesDias})` : "";

  if (dias > 1) return `Faltam ${dias} dias${complemento}`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias${complemento}`;
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  const total = portais.totalExigem || 0;
  const atualizados = portais.anexadosAtualizados || 0;

  if (total <= 0) {
    return {
      key: "nao_exigido",
      label: "Não exigido em portais",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.renovacaoPendente > 0) {
    return {
      key: "renovacao_pendente",
      label: `Renovação pendente em ${portais.renovacaoPendente} ${pluralPortal(
        portais.renovacaoPendente
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (atualizados >= total) {
    return {
      key: "ok",
      label: `Atualizado em ${total} ${pluralPortal(total)}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = Math.max(total - atualizados, 0);

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <FileText size={12} />,
      };
  }
}

function getRastreabilidadeStatus(
  versao: DocumentoVersao,
  documento: DocumentoDetalhe
): RastreabilidadeStatus {
  if (versao.vigente) {
    if (documento.portais.aguardandoValidacao > 0) {
      return "aguardando_validacao";
    }

    return "vigente";
  }

  if (documento.statusValidade === "vencido" && versao.obsoleto) {
    return "vencido";
  }

  if (versao.obsoleto) return "substituido";

  return "substituido";
}

function getRastreabilidadeLabel(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return "Documento vigente";
    case "substituido":
      return "Substituído";
    case "vencido":
      return "Vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "cancelado":
      return "Cancelado";
    default:
      return "Registro";
  }
}

function getRastreabilidadeDescricao(
  status: RastreabilidadeStatus,
  versao: DocumentoVersao
) {
  if (versao.observacao) return versao.observacao;

  switch (status) {
    case "vigente":
      return "Arquivo atualmente utilizado como referência oficial.";
    case "substituido":
      return "Arquivo mantido para rastreabilidade.";
    case "vencido":
      return "Arquivo vencido mantido para histórico.";
    case "aguardando_validacao":
      return "Arquivo anexado, aguardando validação.";
    case "cancelado":
      return "Arquivo cancelado ou desconsiderado.";
    default:
      return "Registro documental.";
  }
}

function getRastreabilidadeVisual(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        dot: "#16a34a",
      };
    case "substituido":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        dot: "#64748b",
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        dot: "#dc2626",
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
        dot: "#7c3aed",
      };
    case "cancelado":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        dot: "#ea580c",
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        dot: "#64748b",
      };
  }
}

function mapDocumentoApi(
  row: DocumentoApi,
  sessaoEmpresaNome?: string | null,
  sessaoEmpresaCnpj?: string | null
): DocumentoDetalhe {
  const portais = row.portais || {};

  return {
    id: toNumber(row.id),
    nome: toNullableString(row.nome) || "Documento sem nome",
    categoria: toNullableString(row.categoria) || "Sem categoria",
    orgaoEmissor: toNullableString(row.orgaoEmissor) || "Não informado",
    empresa: sessaoEmpresaNome || "Empresa não informada",
    cnpj: sessaoEmpresaCnpj || "",
    dataEmissao: normalizeApiDate(row.dataEmissao),
    dataValidade: normalizeApiDate(row.dataValidade),
    dataUpload: normalizeApiDateTime(row.dataUpload || null),
    tipoValidade: normalizeTipoValidade(row.tipoValidade),
    periodicidade: normalizePeriodicidade(row.periodicidade),
    areaResponsavel: toNullableString(row.areaResponsavel) || "Não informado",
    responsavelNome: toNullableString(row.responsavelNome),
    statusValidade: normalizeStatusValidade(row.statusValidade),
    anexoDisponivel: Boolean(row.anexoDisponivel || row.arquivoVigenteId),
    arquivoVigenteId:
      row.arquivoVigenteId === undefined || row.arquivoVigenteId === null
        ? null
        : toNumber(row.arquivoVigenteId, 0),
    arquivoNome: toNullableString(row.arquivoNome),
    s3Key: toNullableString(row.s3Key),
    observacao: null,
    portais: {
      totalExigem: toNumber(portais.totalExigem),
      anexadosAtualizados: toNumber(portais.anexadosAtualizados),
      pendenteAnexo: toNumber(portais.pendenteAnexo),
      vencidos: toNumber(portais.vencidos),
      renovacaoPendente: toNumber(
        portais.renovacaoPendente ?? portais.emRenovacao
      ),
      aguardandoValidacao: toNumber(portais.aguardandoValidacao),
    },
  };
}

function mapArquivoApi(row: DocumentoArquivoApi): DocumentoVersao {
  return {
    id: toNumber(row.id),
    documentoId: toNumber(row.documentoId),
    versao: toNumber(row.versao, 0),
    arquivoNome: toNullableString(row.originalName) || "Arquivo sem nome",
    s3Bucket: toNullableString(row.s3Bucket),
    s3Key: toNullableString(row.s3Key) || "",
    mimeType: toNullableString(row.mimeType),
    dataEmissao: normalizeApiDate(row.dataEmissao),
    dataValidade: normalizeApiDate(row.dataValidade),
    dataUpload: normalizeApiDateTime(row.criadoEm),
    responsavelUpload:
      row.criadoPor !== undefined && row.criadoPor !== null
        ? `Usuário ${row.criadoPor}`
        : "Não informado",
    vigente: Boolean(row.vigente),
    obsoleto: Boolean(row.obsoleto),
    observacao: toNullableString(row.observacao),
  };
}

function groupPortais(
  portais: DocumentoPortalApi[],
  documento: DocumentoDetalhe
) {
  const makeCliente = (portal: DocumentoPortalApi, index: number) => ({
    id: portal.id || index,
    nome:
      portal.empresaClienteId !== undefined && portal.empresaClienteId !== null
        ? `Cliente ${portal.empresaClienteId}`
        : `Portal ${portal.id || index + 1}`,
    portal:
      portal.empresaClientePortalId !== undefined &&
      portal.empresaClientePortalId !== null
        ? `Portal ${portal.empresaClientePortalId}`
        : "Portal do cliente",
    url: null,
    observacao: toNullableString(portal.observacao),
  });

  const exigem = portais
    .filter((item) => item.exigeDocumento !== false)
    .map(makeCliente);

  const atualizados = portais
    .filter((item) => item.statusOperacional === "atualizado")
    .map(makeCliente);

  const pendenteAnexo = portais
    .filter((item) => item.statusOperacional === "pendente_anexo")
    .map(makeCliente);

  const vencidos = portais
    .filter((item) => item.statusOperacional === "vencido")
    .map(makeCliente);

  const renovacaoPendente = portais
    .filter((item) => item.statusOperacional === "renovacao_pendente")
    .map(makeCliente);

  const aguardandoValidacao = portais
    .filter((item) => item.statusOperacional === "aguardando_validacao")
    .map(makeCliente);

  return [
    {
      key: "exigem",
      label: "Portais que exigem",
      value: documento.portais.totalExigem,
      clientes: exigem,
      tone: "neutral" as const,
    },
    {
      key: "atualizados",
      label: "Atualizados",
      value: documento.portais.anexadosAtualizados,
      clientes: atualizados,
      tone: "success" as const,
    },
    {
      key: "pendenteAnexo",
      label: "Pendente anexo",
      value: documento.portais.pendenteAnexo,
      clientes: pendenteAnexo,
      tone: "warning" as const,
    },
    {
      key: "vencidos",
      label: "Vencidos",
      value: documento.portais.vencidos,
      clientes: vencidos,
      tone: "danger" as const,
    },
    {
      key: "renovacaoPendente",
      label: "Renovação pendente",
      value: documento.portais.renovacaoPendente,
      clientes: renovacaoPendente,
      tone: "info" as const,
    },
    {
      key: "aguardandoValidacao",
      label: "Aguardando validação",
      value: documento.portais.aguardandoValidacao,
      clientes: aguardandoValidacao,
      tone: "purple" as const,
    },
  ] satisfies PortalRow[];
}

function getPortalToneStyle(tone: PortalRow["tone"]): { color: string } {
  switch (tone) {
    case "success":
      return { color: "#16a34a" };
    case "warning":
      return { color: "#d97706" };
    case "danger":
      return { color: "#dc2626" };
    case "info":
      return { color: "#2563eb" };
    case "purple":
      return { color: "#7c3aed" };
    case "neutral":
    default:
      return { color: "#475569" };
  }
}

function guessMimeTypeFromName(fileName: string, mimeType?: string | null) {
  const mime = String(mimeType || "")
    .trim()
    .toLowerCase();
  const name = String(fileName || "")
    .trim()
    .toLowerCase();

  if (mime) return mime;

  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".webp")) return "image/webp";

  return "application/octet-stream";
}

function isPdfFile(fileName: string, mimeType?: string | null) {
  const mime = guessMimeTypeFromName(fileName, mimeType);
  const name = String(fileName || "").toLowerCase();

  return mime.includes("pdf") || name.endsWith(".pdf");
}

function isImageFile(fileName: string, mimeType?: string | null) {
  const mime = guessMimeTypeFromName(fileName, mimeType);
  const name = String(fileName || "").toLowerCase();

  return (
    mime.startsWith("image/") ||
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp")
  );
}

const DetailLine: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  return (
    <div style={styles.detailLine}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value || "Não informado"}</span>
    </div>
  );
};

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const DocumentViewerModal: React.FC<{
  viewer: ViewerState;
  departmentColor: string;
  onClose: () => void;
  onDownload: () => void;
}> = ({ viewer, departmentColor, onClose, onDownload }) => {
  if (!viewer.open) return null;

  const mimeType = guessMimeTypeFromName(viewer.fileName, viewer.mimeType);
  const isPdf = viewer.url ? isPdfFile(viewer.fileName, mimeType) : false;
  const isImage = viewer.url ? isImageFile(viewer.fileName, mimeType) : false;

  return (
    <div style={styles.viewerOverlay}>
      <div style={styles.viewerPanel}>
        <div style={styles.viewerHeader}>
          <div style={styles.viewerTitleGroup}>
            <div style={styles.viewerEyebrow}>Visualização do documento</div>
            <div style={styles.viewerTitle}>
              {viewer.fileName || "Documento"}
            </div>
          </div>

          <div style={styles.viewerActions}>
            {viewer.url && (
              <a
                href={viewer.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...styles.viewerActionButton,
                  color: departmentColor,
                  borderColor: toRgba(departmentColor, 0.3),
                  background: toRgba(departmentColor, 0.06),
                  textDecoration: "none",
                }}
              >
                <ExternalLink size={13} />
                Abrir em aba
              </a>
            )}

            {viewer.url && (
              <button
                type="button"
                style={{
                  ...styles.viewerActionButton,
                  color: departmentColor,
                  borderColor: toRgba(departmentColor, 0.3),
                  background: "#ffffff",
                }}
                onClick={onDownload}
              >
                <Download size={13} />
                Baixar
              </button>
            )}

            <button
              type="button"
              style={styles.viewerCloseButton}
              onClick={onClose}
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div style={styles.viewerBody}>
          {viewer.loading && (
            <div style={styles.viewerStateBox}>
              <Loader2 size={26} color={departmentColor} />
              <span>Carregando documento...</span>
            </div>
          )}

          {!viewer.loading && viewer.error && (
            <div style={styles.viewerErrorBox}>
              <AlertTriangle size={20} />
              <div>
                <strong>Não foi possível visualizar o documento.</strong>
                <span>{viewer.error}</span>
              </div>
            </div>
          )}

          {!viewer.loading && !viewer.error && viewer.url && isPdf && (
            <iframe
              src={viewer.url}
              title={viewer.fileName || "Documento PDF"}
              style={styles.viewerIframe}
            />
          )}

          {!viewer.loading && !viewer.error && viewer.url && isImage && (
            <div style={styles.viewerImageWrap}>
              <img
                src={viewer.url}
                alt={viewer.fileName || "Documento"}
                style={styles.viewerImage}
              />
            </div>
          )}

          {!viewer.loading &&
            !viewer.error &&
            viewer.url &&
            !isPdf &&
            !isImage && (
              <div style={styles.viewerStateBox}>
                <FileText size={28} color={departmentColor} />
                <strong>
                  Pré-visualização não disponível para este formato.
                </strong>
                <span>
                  Use “Abrir em aba” ou “Baixar” para acessar o arquivo.
                </span>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

const CertidaoDocumentoDetalhe: React.FC = () => {
  const { departmentColor } = useTheme();
  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const { sessao } = useAuth();
  const navigate = useNavigate();
  const { documentoId } = useParams<{ documentoId: string }>();

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);

  const empresaNome =
    sessao?.empresa?.razao_social ||
    sessao?.empresa_razao_social ||
    "Empresa não informada";

  const empresaCnpj = sessao?.empresa?.cnpj || sessao?.empresa_cnpj || "";

  const [documento, setDocumento] = useState<DocumentoDetalhe | null>(null);
  const [versoes, setVersoes] = useState<DocumentoVersao[]>([]);
  const [portais, setPortais] = useState<DocumentoPortalApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadObservacao, setUploadObservacao] = useState("");
  const [uploadDataEmissao, setUploadDataEmissao] = useState("");
  const [uploadDataValidade, setUploadDataValidade] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErro, setUploadErro] = useState<string | null>(null);

  const [expandedPortalKey, setExpandedPortalKey] = useState<string | null>(
    null
  );

  const [viewer, setViewer] = useState<ViewerState>(initialViewerState);

  const idNumber = Number(documentoId);

  const canUpload = Boolean(selectedFile && documento && empresaId && moduloId);

  const rastreabilidade = useMemo<DocumentoRastreabilidade[]>(() => {
    if (!documento) return [];

    return versoes.map((versao) => {
      const statusRastreabilidade = getRastreabilidadeStatus(versao, documento);

      return {
        ...versao,
        statusRastreabilidade,
        titulo: getRastreabilidadeLabel(statusRastreabilidade),
        descricaoStatus: getRastreabilidadeDescricao(
          statusRastreabilidade,
          versao
        ),
      };
    });
  }, [documento, versoes]);

  const versaoVigente = useMemo(() => {
    return versoes.find((item) => item.vigente) || null;
  }, [versoes]);

  useEffect(() => {
    let cancelled = false;

    const carregarDetalhe = async () => {
      setLoading(true);
      setErro(null);

      try {
        if (!Number.isFinite(idNumber)) {
          throw new Error("documento_id inválido.");
        }

        if (!empresaId) {
          throw new Error(
            "empresa_id não encontrado no contexto de autenticação."
          );
        }

        if (!moduloId) {
          throw new Error(
            "modulo_id não encontrado no contexto. Verifique o vínculo do módulo no menu/permissões."
          );
        }

        const params = new URLSearchParams();
        params.set("empresa_id", String(empresaId));
        params.set("modulo_id", String(moduloId));

        const response = await fetch(
          `${API_DOCUMENTOS_URL}/${idNumber}?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const data = (await response
          .json()
          .catch(() => null)) as ApiDetalheResponse | null;

        if (!response.ok || !data?.ok || !data.documento) {
          throw new Error(
            data?.erro ||
              `Falha ao carregar documento. Status HTTP ${response.status}`
          );
        }

        const mappedDocumento = mapDocumentoApi(
          data.documento,
          empresaNome,
          empresaCnpj
        );

        const mappedVersoes = Array.isArray(data.rastreabilidade)
          ? data.rastreabilidade
              .map(mapArquivoApi)
              .sort((a, b) => b.versao - a.versao)
          : [];

        if (!cancelled) {
          setDocumento(mappedDocumento);
          setVersoes(mappedVersoes);
          setPortais(Array.isArray(data.portais) ? data.portais : []);
        }
      } catch (error: any) {
        if (!cancelled) {
          setErro(error?.message || "Falha ao carregar documento.");
          setDocumento(null);
          setVersoes([]);
          setPortais([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    carregarDetalhe();

    return () => {
      cancelled = true;
    };
  }, [idNumber, empresaId, moduloId, empresaNome, empresaCnpj, reloadKey]);

  const gerarUrlArquivo = async (arquivoId?: number | null) => {
    if (!arquivoId) {
      throw new Error("arquivo_id inválido.");
    }

    if (!empresaId || !moduloId) {
      throw new Error("Contexto de empresa/módulo não encontrado.");
    }

    const params = new URLSearchParams();
    params.set("empresa_id", String(empresaId));
    params.set("modulo_id", String(moduloId));

    const response = await fetch(
      `${API_DOCUMENTOS_URL}/anexos/${arquivoId}/url?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = (await response
      .json()
      .catch(() => null)) as ApiAnexoUrlResponse | null;

    if (!response.ok || !data?.ok || !data.url) {
      throw new Error(
        data?.erro ||
          `Falha ao gerar URL do anexo. Status HTTP ${response.status}`
      );
    }

    return data;
  };

  const handlePreviewArquivo = async (arquivoId?: number | null) => {
    const fileName =
      versaoVigente?.arquivoNome ||
      documento?.arquivoNome ||
      documento?.s3Key ||
      "Documento";

    const mimeType =
      versaoVigente?.mimeType ||
      guessMimeTypeFromName(fileName, versaoVigente?.mimeType);

    setViewer({
      open: true,
      loading: true,
      url: null,
      mimeType,
      fileName,
      error: null,
    });

    try {
      const data = await gerarUrlArquivo(arquivoId);
      const finalFileName =
        data.arquivo?.originalName || fileName || "Documento";
      const finalMimeType =
        data.arquivo?.mimeType ||
        mimeType ||
        guessMimeTypeFromName(finalFileName, null);

      setViewer({
        open: true,
        loading: false,
        url: data.url || null,
        mimeType: finalMimeType,
        fileName: finalFileName,
        error: null,
      });
    } catch (error: any) {
      setViewer((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || "Falha ao visualizar anexo.",
      }));
    }
  };

  const handleDownloadArquivo = async (arquivoId?: number | null) => {
    try {
      setErro(null);

      const data = await gerarUrlArquivo(arquivoId);

      if (!data.url) {
        throw new Error("URL temporária do anexo não retornada pela API.");
      }

      const fileName =
        data.arquivo?.originalName ||
        versaoVigente?.arquivoNome ||
        documento?.arquivoNome ||
        "documento";

      const response = await fetch(data.url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `Falha ao baixar arquivo do S3. Status HTTP ${response.status}`
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();

      window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error: any) {
      setErro(error?.message || "Falha ao baixar anexo.");
    }
  };

  const closeViewer = () => {
    setViewer(initialViewerState);
  };

  const handleUpload = async () => {
    if (uploading) return;

    setUploadErro(null);

    try {
      if (!documento) {
        throw new Error("Documento não carregado.");
      }

      if (!empresaId || !moduloId) {
        throw new Error("Contexto de empresa/módulo não encontrado.");
      }

      if (!selectedFile) {
        throw new Error("Selecione um arquivo para anexar.");
      }

      if (
        documento.tipoValidade !== "sem_vencimento" &&
        documento.tipoValidade !== "sob_demanda" &&
        !String(uploadDataValidade ?? "").trim()
      ) {
        throw new Error("Informe a data de vencimento do documento.");
      }

      setUploading(true);

      const file = selectedFile;

      const presignResponse = await fetch(API_PRESIGN_UPLOAD_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documento.id,
          mime_type: file.type || "application/octet-stream",
          original_name: file.name,
          size_bytes: file.size,
        }),
      });

      const presignData = (await presignResponse
        .json()
        .catch(() => null)) as ApiPresignUploadResponse | null;

      if (!presignResponse.ok || !presignData?.ok || !presignData.uploadUrl) {
        throw new Error(
          presignData?.erro ||
            `Falha ao preparar upload. Status HTTP ${presignResponse.status}`
        );
      }

      const uploadResponse = await fetch(presignData.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Falha ao enviar arquivo ao S3. Status HTTP ${uploadResponse.status}`
        );
      }

      const registrarResponse = await fetch(API_REGISTRAR_ANEXO_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documento.id,
          s3_bucket: presignData.bucket,
          s3_key: presignData.key,
          mime_type: file.type || "application/octet-stream",
          original_name: file.name,
          size_bytes: file.size,
          data_emissao: uploadDataEmissao || null,
          data_validade:
            documento.tipoValidade === "sem_vencimento" ||
            documento.tipoValidade === "sob_demanda"
              ? null
              : uploadDataValidade || null,
          observacao: String(uploadObservacao ?? "").trim() || null,
        }),
      });

      const registrarData = (await registrarResponse
        .json()
        .catch(() => null)) as ApiRegistrarAnexoResponse | null;

      if (!registrarResponse.ok || !registrarData?.ok) {
        throw new Error(
          registrarData?.erro ||
            `Falha ao registrar anexo. Status HTTP ${registrarResponse.status}`
        );
      }

      setSelectedFile(null);
      setUploadObservacao("");
      setUploadDataEmissao("");
      setUploadDataValidade("");
      setReloadKey((prev) => prev + 1);
    } catch (error: any) {
      setUploadErro(error?.message || "Falha ao anexar documento.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <TitleButtons fullBleedLine title="Certidões e Documentos - Detalhe" />

        <div style={styles.content}>
          <section style={styles.emptyPanel}>
            <Loader2 size={26} color={activeDepartmentColor} />

            <div>
              <h2 style={styles.emptyTitle}>Carregando documento</h2>
              <p style={styles.emptyText}>
                Consultando dados de governança documental.
              </p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!documento) {
    return (
      <div style={styles.page}>
        <TitleButtons fullBleedLine title="Documento não encontrado" />

        <div style={styles.content}>
          <section style={styles.emptyPanel}>
            <FileText size={28} color={activeDepartmentColor} />

            <div>
              <h2 style={styles.emptyTitle}>Documento não encontrado</h2>
              <p style={styles.emptyText}>
                {erro ||
                  "O documento solicitado não existe ou não está disponível."}
              </p>
            </div>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate(`${BASE_PATH}/visao-geral`)}
            >
              Voltar para a listagem
            </button>
          </section>
        </div>
      </div>
    );
  }

  const statusValidadeVisual = getStatusValidadeVisual(
    documento.statusValidade
  );

  const operacional = getOperacionalVisual(documento.portais);
  const prazoOperacional = getPrazoOperacional(documento);
  const portalRows = groupPortais(portais, documento);

  return (
    <div style={styles.page}>
      <TitleButtons fullBleedLine title="Certidões e Documentos - Detalhe" />

      <div style={styles.content}>
        <section style={styles.backBar}>
          <button
            type="button"
            style={{
              ...styles.backButton,
              color: activeDepartmentColor,
            }}
            onClick={() => navigate(`${BASE_PATH}/visao-geral`)}
          >
            <ArrowLeft size={15} />
            <span>Voltar</span>
          </button>
        </section>

        {erro && (
          <section style={styles.errorBox}>
            <AlertTriangle size={16} />

            <div>
              <strong>Aviso</strong>
              <span>{erro}</span>
            </div>
          </section>
        )}

        <section style={styles.headerCard}>
          <div
            style={{
              ...styles.headerIcon,
              borderColor: activeDepartmentColor,
              color: activeDepartmentColor,
              background: toRgba(activeDepartmentColor, 0.08),
            }}
          >
            <FileText size={22} />
          </div>

          <div style={styles.headerContent}>
            <div style={styles.overline}>Documento corporativo</div>
            <h1 style={styles.title}>{documento.nome}</h1>

            <div style={styles.metaLine}>
              <span>{documento.categoria}</span>
              <span>•</span>
              <span>{documento.orgaoEmissor}</span>
              <span>•</span>
              <span>{documento.cnpj || documento.empresa}</span>
            </div>
          </div>

          <div style={styles.statusColumn}>
            <Badge
              background={statusValidadeVisual.background}
              color={statusValidadeVisual.color}
              border={statusValidadeVisual.border}
              icon={statusValidadeVisual.icon}
            >
              {getStatusValidadeLabel(documento.statusValidade)}
            </Badge>
          </div>
        </section>

        <section style={styles.mainGrid}>
          <div style={styles.leftColumn}>
            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Anexo vigente</h2>
                <span style={styles.panelHint}>Arquivo oficial atual</span>
              </div>

              <div
                style={{
                  ...styles.fileCard,
                  ...(documento.anexoDisponivel
                    ? styles.fileCardActive
                    : styles.fileCardPending),
                }}
              >
                <div
                  style={{
                    ...styles.fileIcon,
                    ...(documento.anexoDisponivel
                      ? styles.fileIconActive
                      : styles.fileIconPending),
                  }}
                >
                  {documento.anexoDisponivel ? (
                    <CheckCircle2 size={18} />
                  ) : (
                    <Paperclip size={18} />
                  )}
                </div>

                <div style={styles.fileInfo}>
                  <div style={styles.fileName}>
                    {versaoVigente?.arquivoNome ||
                      documento.arquivoNome ||
                      documento.s3Key ||
                      "Nenhum arquivo vinculado"}
                  </div>

                  <div style={styles.fileMeta}>
                    {documento.anexoDisponivel
                      ? `Anexado em ${
                          versaoVigente?.dataUpload ||
                          documento.dataUpload ||
                          "data não informada"
                        } por ${
                          versaoVigente?.responsavelUpload ||
                          "responsável não informado"
                        }`
                      : "Documento pendente de anexo"}
                  </div>
                </div>

                <div style={styles.fileActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.fileActionButton,
                      opacity: documento.arquivoVigenteId ? 1 : 0.5,
                      cursor: documento.arquivoVigenteId
                        ? "pointer"
                        : "not-allowed",
                    }}
                    disabled={!documento.arquivoVigenteId}
                    onClick={() =>
                      handlePreviewArquivo(documento.arquivoVigenteId)
                    }
                  >
                    <FileText size={13} />
                    Visualizar
                  </button>

                  <button
                    type="button"
                    style={{
                      ...styles.fileActionButton,
                      opacity: documento.arquivoVigenteId ? 1 : 0.5,
                      cursor: documento.arquivoVigenteId
                        ? "pointer"
                        : "not-allowed",
                    }}
                    disabled={!documento.arquivoVigenteId}
                    onClick={() =>
                      handleDownloadArquivo(documento.arquivoVigenteId)
                    }
                  >
                    <Download size={13} />
                    Baixar
                  </button>

                  <span
                    style={{
                      ...styles.fileStatus,
                      ...(documento.anexoDisponivel
                        ? styles.fileStatusOk
                        : styles.fileStatusPending),
                    }}
                  >
                    {documento.anexoDisponivel
                      ? "Ativo atualmente"
                      : "Pendente"}
                  </span>
                </div>
              </div>

              <div style={styles.currentDocumentCompact}>
                <div style={styles.currentMainStatus}>
                  <div style={styles.currentStatusHeader}>
                    <span
                      style={{
                        ...styles.currentStatusBadge,
                        ...(documento.statusValidade === "regular"
                          ? styles.currentStatusRegular
                          : documento.statusValidade === "a_vencer"
                          ? styles.currentStatusWarning
                          : documento.statusValidade === "vencido"
                          ? styles.currentStatusDanger
                          : styles.currentStatusNeutral),
                      }}
                    >
                      {getStatusValidadeLabel(documento.statusValidade)}
                    </span>

                    <span style={styles.currentPeriodicityBadge}>
                      {getPeriodicidadeLabel(documento.periodicidade)}
                    </span>

                    <span style={styles.currentTypeBadge}>
                      {getTipoValidadeLabel(documento.tipoValidade)}
                    </span>
                  </div>

                  <div style={styles.currentStatusLine}>
                    <span>
                      Emissão:{" "}
                      <strong>
                        {formatNullableDate(
                          versaoVigente?.dataEmissao || documento.dataEmissao
                        )}
                      </strong>
                    </span>

                    <span>•</span>

                    <span>
                      Validade:{" "}
                      <strong>
                        {formatNullableDate(
                          versaoVigente?.dataValidade || documento.dataValidade
                        )}
                      </strong>
                    </span>

                    <span>•</span>

                    <span
                      style={{
                        ...styles.currentPrazoText,
                        ...(documento.statusValidade === "a_vencer" ||
                        documento.statusValidade === "vencido"
                          ? styles.currentPrazoTextDanger
                          : {}),
                      }}
                    >
                      {prazoOperacional}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.uploadArea}>
                {uploadErro && (
                  <div style={styles.uploadError}>
                    <AlertTriangle size={14} />
                    <span>{uploadErro}</span>
                  </div>
                )}

                <label style={styles.uploadLabel}>
                  <UploadCloud size={17} />
                  <span>Selecionar documento atualizado</span>

                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    style={styles.fileInput}
                    disabled={uploading}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setSelectedFile(file);
                    }}
                  />
                </label>

                {selectedFile && (
                  <div style={styles.selectedFile}>
                    Selecionado: {selectedFile.name}
                  </div>
                )}

                <div style={styles.uploadDateGrid}>
                  <div style={styles.uploadDateField}>
                    <label style={styles.uploadDateLabel}>Emissão</label>
                    <input
                      type="date"
                      value={uploadDataEmissao}
                      onChange={(event) =>
                        setUploadDataEmissao(event.target.value)
                      }
                      style={styles.uploadDateInput}
                      disabled={uploading}
                    />
                  </div>

                  <div style={styles.uploadDateField}>
                    <label style={styles.uploadDateLabel}>
                      Validade{" "}
                      {documento.tipoValidade !== "sem_vencimento" &&
                      documento.tipoValidade !== "sob_demanda"
                        ? "*"
                        : ""}
                    </label>
                    <input
                      type="date"
                      value={uploadDataValidade}
                      onChange={(event) =>
                        setUploadDataValidade(event.target.value)
                      }
                      style={styles.uploadDateInput}
                      disabled={
                        uploading ||
                        documento.tipoValidade === "sem_vencimento" ||
                        documento.tipoValidade === "sob_demanda"
                      }
                    />
                  </div>
                </div>

                <textarea
                  value={uploadObservacao}
                  onChange={(event) => setUploadObservacao(event.target.value)}
                  placeholder="Observação sobre o novo documento, substituição ou conferência..."
                  style={styles.textarea}
                  disabled={uploading}
                />

                <button
                  type="button"
                  style={{
                    ...styles.uploadButton,
                    background: canUpload ? activeDepartmentColor : "#94a3b8",
                    borderColor: canUpload ? activeDepartmentColor : "#94a3b8",
                  }}
                  disabled={!canUpload || uploading}
                  onClick={handleUpload}
                >
                  {uploading ? (
                    <Loader2 size={14} />
                  ) : (
                    <UploadCloud size={14} />
                  )}
                  Registrar novo anexo vigente
                </button>
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Rastreabilidade documental</h2>
                <span style={styles.panelHint}>Cadeia de substituição</span>
              </div>

              <div style={styles.traceabilityListCompact}>
                {rastreabilidade.map((registro) => {
                  const visual = getRastreabilidadeVisual(
                    registro.statusRastreabilidade
                  );

                  return (
                    <div
                      key={registro.id}
                      style={{
                        ...styles.traceabilityRowCompact,
                        borderColor: visual.border,
                        background:
                          registro.statusRastreabilidade === "vigente"
                            ? "#fbfffd"
                            : registro.statusRastreabilidade === "vencido"
                            ? "#fffafa"
                            : registro.statusRastreabilidade ===
                              "aguardando_validacao"
                            ? "#fbfaff"
                            : "#ffffff",
                      }}
                    >
                      <div style={styles.traceabilityStatusColumn}>
                        <span
                          style={{
                            ...styles.traceabilityDotCompact,
                            background: visual.dot,
                          }}
                        />

                        <span
                          style={{
                            ...styles.traceabilityStatusCompact,
                            background: visual.background,
                            color: visual.color,
                            borderColor: visual.border,
                          }}
                        >
                          {registro.titulo}
                        </span>
                      </div>

                      <div style={styles.traceabilityFileColumn}>
                        <div style={styles.traceabilityFileTop}>
                          <span style={styles.traceabilityFileNameCompact}>
                            {registro.arquivoNome}
                          </span>

                          {registro.vigente && (
                            <span style={styles.traceabilityCurrentTag}>
                              Atual
                            </span>
                          )}
                        </div>

                        <div style={styles.traceabilityMetaCompact}>
                          <span>
                            Emissão: {formatNullableDate(registro.dataEmissao)}
                          </span>
                          <span>•</span>
                          <span>
                            Validade:{" "}
                            {formatNullableDate(registro.dataValidade)}
                          </span>
                          <span>•</span>
                          <span>Upload: {registro.dataUpload}</span>
                          <span>•</span>
                          <span>{registro.responsavelUpload}</span>
                        </div>

                        {registro.descricaoStatus && (
                          <div style={styles.traceabilityObservationCompact}>
                            {registro.descricaoStatus}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {rastreabilidade.length === 0 && (
                  <div style={styles.emptySmall}>
                    Nenhum registro documental vinculado.
                  </div>
                )}
              </div>
            </section>
          </div>

          <div style={styles.rightColumn}>
            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Informações principais</h2>
                <span style={styles.panelHint}>Resumo</span>
              </div>

              <div style={styles.detailList}>
                <DetailLine label="Empresa" value={documento.empresa} />
                <DetailLine
                  label="Área responsável"
                  value={documento.areaResponsavel}
                />
                <DetailLine
                  label="Responsável"
                  value={documento.responsavelNome}
                />
                <DetailLine label="Emissão" value={documento.dataEmissao} />
                <DetailLine label="Validade" value={documento.dataValidade} />
                <DetailLine label="Prazo" value={prazoOperacional} />
                <DetailLine
                  label="Tipo"
                  value={getTipoValidadeLabel(documento.tipoValidade)}
                />
                <DetailLine
                  label="Periodicidade"
                  value={getPeriodicidadeLabel(documento.periodicidade)}
                />
              </div>
            </section>

            <section style={styles.panel}>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>Situação nos portais</h2>
                <span style={styles.panelHint}>Consolidado</span>
              </div>

              <div style={styles.portalStatusBox}>
                <div style={styles.portalStatusBadges}>
                  <Badge background="#ecfdf3" color="#166534" border="#bbf7d0">
                    Atualizado em {documento.portais.anexadosAtualizados}{" "}
                    {pluralPortal(documento.portais.anexadosAtualizados)}
                  </Badge>

                  {operacional.key !== "ok" &&
                    operacional.key !== "nao_exigido" && (
                      <Badge
                        background={operacional.background}
                        color={operacional.color}
                        border={operacional.border}
                      >
                        {operacional.label}
                      </Badge>
                    )}

                  {operacional.key === "ok" && (
                    <Badge
                      background={operacional.background}
                      color={operacional.color}
                      border={operacional.border}
                    >
                      Tudo atualizado
                    </Badge>
                  )}
                </div>

                <div style={styles.portalStatusText}>
                  Consolidação dos portais externos que exigem este documento.
                </div>
              </div>

              <div style={styles.portalSummaryCompact}>
                {portalRows.map((row) => {
                  const isExpanded = expandedPortalKey === row.key;
                  const toneStyle = getPortalToneStyle(row.tone);

                  return (
                    <div key={row.key} style={styles.portalExpandableItem}>
                      <button
                        type="button"
                        style={{
                          ...styles.portalExpandableButton,
                          ...(isExpanded
                            ? styles.portalExpandableButtonActive
                            : {}),
                        }}
                        onClick={() =>
                          setExpandedPortalKey(isExpanded ? null : row.key)
                        }
                      >
                        <div style={styles.portalExpandableLeft}>
                          <span
                            style={{
                              ...styles.portalToneDot,
                              background: toneStyle.color,
                            }}
                          />

                          <span style={styles.portalExpandableLabel}>
                            {row.label}
                          </span>
                        </div>

                        <div style={styles.portalExpandableRight}>
                          <strong
                            style={{
                              ...styles.portalExpandableValue,
                              color: toneStyle.color,
                            }}
                          >
                            {row.value}
                          </strong>

                          <span
                            style={{
                              ...styles.portalChevron,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              color: toneStyle.color,
                            }}
                          >
                            ▾
                          </span>
                        </div>
                      </button>

                      {isExpanded && (
                        <div style={styles.portalClientsBox}>
                          {row.clientes.length > 0 ? (
                            row.clientes.map((cliente) => (
                              <div
                                key={cliente.id}
                                style={styles.portalClientLine}
                              >
                                <div style={styles.portalClientInfo}>
                                  <span style={styles.portalClientName}>
                                    {cliente.nome}
                                  </span>

                                  <span style={styles.portalClientMeta}>
                                    {cliente.portal}
                                    {cliente.observacao
                                      ? ` • ${cliente.observacao}`
                                      : ""}
                                  </span>
                                </div>

                                {cliente.url ? (
                                  <button
                                    type="button"
                                    style={styles.portalOpenButton}
                                    onClick={() => {
                                      window.open(
                                        cliente.url || "",
                                        "_blank",
                                        "noopener,noreferrer"
                                      );
                                    }}
                                  >
                                    <ExternalLink size={11} />
                                    Abrir
                                  </button>
                                ) : (
                                  <span style={styles.portalNoLink}>
                                    Sem link
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div style={styles.portalEmptyClients}>
                              Nenhum portal nesta situação.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </section>
      </div>

      <DocumentViewerModal
        viewer={viewer}
        departmentColor={activeDepartmentColor}
        onClose={closeViewer}
        onDownload={() => handleDownloadArquivo(documento.arquivoVigenteId)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 700,
  },

  fileActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    minWidth: "fit-content",
  },

  fileActionButton: {
    height: 24,
    padding: "0 8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 11,
    fontWeight: 850,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  currentDocumentCompact: {
    margin: "0 12px 12px",
    border: "1px solid #e5e7eb",
    background: "#FFFFFF",
  },

  currentMainStatus: {
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  currentStatusHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },

  currentStatusBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  currentStatusRegular: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },

  currentStatusWarning: {
    background: "#fffbeb",
    color: "#92400e",
    borderColor: "#fde68a",
  },

  currentStatusDanger: {
    background: "#fef2f2",
    color: "#991b1b",
    borderColor: "#fecaca",
  },

  currentStatusNeutral: {
    background: "#f8fafc",
    color: "#475569",
    borderColor: "#cbd5e1",
  },

  currentPeriodicityBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  currentTypeBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid #dbe4ef",
    background: "#FFFFFF",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  currentStatusLine: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.35,
  },

  currentPrazoText: {
    fontSize: 12.2,
    fontWeight: 900,
    color: "#334155",
  },

  currentPrazoTextDanger: {
    color: "#b91c1c",
  },

  backBar: {
    minHeight: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    background: "transparent",
    border: "none",
    padding: "0 2px",
  },

  backButton: {
    height: 28,
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12.5,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  secondaryButton: {
    height: 31,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "inherit",
  },

  headerCard: {
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
  },

  headerIcon: {
    width: 42,
    height: 42,
    background: "rgba(47, 63, 89, 0.08)",
    border: `1px solid ${DEPARTMENT_COLOR}`,
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  headerContent: {
    minWidth: 0,
  },

  overline: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  title: {
    margin: "2px 0 0",
    fontSize: 18,
    fontWeight: 900,
    color: "#111827",
    lineHeight: 1.2,
  },

  metaLine: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
  },

  statusColumn: {
    display: "flex",
    alignItems: "flex-end",
    flexDirection: "column",
    gap: 6,
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.25fr) minmax(360px, 0.75fr)",
    gap: 10,
    alignItems: "start",
  },

  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },

  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },

  panel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  panelHeader: {
    minHeight: 42,
    padding: "9px 12px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  panelTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 900,
    color: "#111827",
  },

  panelHint: {
    fontSize: 11,
    fontWeight: 800,
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  fileCard: {
    margin: 12,
    padding: 12,
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 10,
  },

  fileCardActive: {
    borderColor: "#bbf7d0",
    background: "#fbfffd",
  },

  fileCardPending: {
    borderColor: "#fed7aa",
    background: "#fffaf5",
  },

  fileIcon: {
    width: 34,
    height: 34,
    background: "#f2f5f8",
    border: "1px solid #e2e8f0",
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  fileIconActive: {
    background: "#ecfdf3",
    borderColor: "#bbf7d0",
    color: "#16a34a",
  },

  fileIconPending: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
    color: "#9a3412",
  },

  fileInfo: {
    minWidth: 0,
  },

  fileName: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  fileMeta: {
    marginTop: 2,
    fontSize: 11.5,
    fontWeight: 650,
    color: "#64748b",
  },

  fileStatus: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  fileStatusOk: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },

  fileStatusPending: {
    background: "#fff7ed",
    color: "#9a3412",
    borderColor: "#fed7aa",
  },

  uploadArea: {
    margin: "0 12px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  uploadError: {
    minHeight: 32,
    padding: "7px 9px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 11.8,
    fontWeight: 800,
  },

  uploadLabel: {
    minHeight: 42,
    border: "1px dashed #94a3b8",
    background: "#f8fafc",
    color: DEPARTMENT_COLOR,
    fontSize: 12.5,
    fontWeight: 850,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
  },

  fileInput: {
    display: "none",
  },

  selectedFile: {
    fontSize: 11.8,
    fontWeight: 800,
    color: "#334155",
  },

  uploadDateGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  uploadDateField: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  uploadDateLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  uploadDateInput: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 8px",
    fontSize: 12,
    fontWeight: 750,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    minHeight: 62,
    resize: "vertical",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    padding: "8px 9px",
    fontSize: 12,
    fontWeight: 650,
    color: "#111827",
    outline: "none",
    fontFamily: "inherit",
  },

  uploadButton: {
    height: 32,
    padding: "0 11px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    fontFamily: "inherit",
  },

  traceabilityListCompact: {
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  traceabilityRowCompact: {
    minHeight: 48,
    padding: "8px 10px",
    border: "1px solid",
    display: "grid",
    gridTemplateColumns: "145px minmax(0, 1fr)",
    gap: 10,
    alignItems: "center",
  },

  traceabilityStatusColumn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  traceabilityDotCompact: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },

  traceabilityStatusCompact: {
    minHeight: 21,
    padding: "0 7px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 10.8,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  traceabilityFileColumn: {
    minWidth: 0,
  },

  traceabilityFileTop: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  traceabilityFileNameCompact: {
    fontSize: 12.3,
    fontWeight: 900,
    color: DEPARTMENT_COLOR,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  traceabilityCurrentTag: {
    minHeight: 18,
    padding: "0 6px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#64748b",
    fontSize: 10,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  traceabilityMetaCompact: {
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.25,
  },

  traceabilityObservationCompact: {
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 750,
    color: "#475569",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailList: {
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  detailLine: {
    minHeight: 34,
    borderBottom: "1px solid #eef2f5",
    display: "grid",
    gridTemplateColumns: "130px minmax(0, 1fr)",
    alignItems: "center",
    gap: 10,
  },

  detailLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  detailValue: {
    fontSize: 12.3,
    fontWeight: 750,
    color: "#111827",
    lineHeight: 1.25,
  },

  portalStatusBox: {
    padding: 12,
    borderBottom: "1px solid #eef2f5",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 7,
  },

  portalStatusBadges: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },

  portalStatusText: {
    fontSize: 11.8,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  portalSummaryCompact: {
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  portalExpandableItem: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  portalExpandableButton: {
    width: "100%",
    minHeight: 32,
    border: "none",
    background: "#ffffff",
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    outline: "none",
    boxShadow: "none",
  },

  portalExpandableButtonActive: {
    background: "#fbfdff",
  },

  portalExpandableLeft: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },

  portalToneDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },

  portalExpandableLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalExpandableRight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    flexShrink: 0,
  },

  portalExpandableValue: {
    fontSize: 12.5,
    fontWeight: 950,
  },

  portalChevron: {
    width: 14,
    height: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    transition: "transform 160ms ease",
  },

  portalClientsBox: {
    padding: "6px 8px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    background: "#fbfdff",
    borderTop: "1px solid #eef2f5",
  },

  portalClientLine: {
    minHeight: 31,
    padding: "5px 6px",
    border: "1px solid #eef2f5",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 8,
  },

  portalClientInfo: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  portalClientName: {
    fontSize: 11.8,
    fontWeight: 850,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalClientMeta: {
    fontSize: 10.8,
    fontWeight: 700,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalOpenButton: {
    height: 23,
    padding: "0 7px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 10.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },

  portalNoLink: {
    color: "#94a3b8",
    fontSize: 10.8,
    fontWeight: 800,
  },

  portalEmptyClients: {
    padding: "7px 6px",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#64748b",
    textAlign: "center",
  },

  emptySmall: {
    padding: 12,
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textAlign: "center",
  },

  emptyPanel: {
    minHeight: 160,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 10,
    textAlign: "center",
    padding: 24,
  },

  emptyTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 900,
    color: "#111827",
  },

  emptyText: {
    margin: "4px 0 0",
    fontSize: 12.5,
    fontWeight: 650,
    color: "#64748b",
  },

  viewerOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.64)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  viewerPanel: {
    width: "min(1180px, 100%)",
    height: "min(860px, calc(100vh - 36px))",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.34)",
    display: "grid",
    gridTemplateRows: "48px minmax(0, 1fr)",
    overflow: "hidden",
  },

  viewerHeader: {
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  viewerTitleGroup: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  viewerEyebrow: {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.35,
  },

  viewerTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 620,
  },

  viewerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  viewerActionButton: {
    height: 28,
    padding: "0 9px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11.5,
    fontWeight: 850,
    fontFamily: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  viewerCloseButton: {
    width: 30,
    height: 30,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  viewerBody: {
    minHeight: 0,
    background: "#eef2f6",
    position: "relative",
    overflow: "hidden",
  },

  viewerIframe: {
    width: "100%",
    height: "100%",
    border: "none",
    background: "#ffffff",
  },

  viewerImageWrap: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    boxSizing: "border-box",
  },

  viewerImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
  },

  viewerStateBox: {
    height: "100%",
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 8,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 800,
    textAlign: "center",
    padding: 24,
    boxSizing: "border-box",
  },

  viewerErrorBox: {
    margin: 18,
    padding: 14,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    fontSize: 12.3,
    fontWeight: 750,
  },
};

export default CertidaoDocumentoDetalhe;
